import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  IconButton,
  Paper,
  TableContainer,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Table,
  Stack,
  Button,
  Tooltip,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import { isEmpty } from "lodash";

import Swal from "sweetalert2";
import { DataContext } from "../../../context/DataContext";
import { deleteToken, deleteUserData, getToken } from "../../../api/Account";
import { getRuta, isAccess, simpleMessage } from "../../../helpers/Helpers";

import NoData from "../../../components/NoData";
import SearchField from "../../../components/ui/SearchField";
import ListFooter from "../../../components/ui/ListFooter";
import { deleteRolByIdAsync, getRolListAsync } from "../../../api/Rols";
import { getTokens } from "../../../theme";

const RolList = () => {
  const [rolList, setRolList] = useState([]);

  const [localReload, setLocalReload] = useState(false);
  const { setIsLoading, setIsLogged, setIsUnautorized, access, isDarkMode } =
    useContext(DataContext);
  const t = getTokens(isDarkMode);
  let ruta = getRuta();
  const token = getToken();
  let navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");

  const withSearch = rolList.filter((val) => {
    if (searchTerm === "") {
      return val;
    } else if (
      val.id.toString().includes(searchTerm) ||
      (val.rolName || "").toString().includes(searchTerm)
    ) {
      return val;
    }
    return false;
  });

  useEffect(() => {
    (async () => {
      setIsLoading(true);

      const result = await getRolListAsync(token);

      if (!result.statusResponse) {
        setIsLoading(false);
        if (result.error === "Unauthorized") {
          setIsUnautorized(true);
          return;
        }
        simpleMessage(result.error, "error");
        return;
      }

      setIsLoading(false);
      setRolList(result.data.result);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localReload]);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsperPage] = useState(10);
  const indexLast = currentPage * itemsperPage;
  const indexFirst = indexLast - itemsperPage;
  const currentItem = withSearch.slice(indexFirst, indexLast);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const onChangeSearch = (val) => {
    setCurrentPage(1);
    setSearchTerm(val.toUpperCase());
    paginate(1);
  };

  const deleteUserRol = async (item) => {
    Swal.fire({
      title: "¿Estas Seguro?",
      text: "No podras revertir esto...!",
      icon: "warning",
      showCancelButton: true,

      confirmButtonColor: "#1d4ed8",
      cancelButtonColor: "#d83a3f",

      confirmButtonText: "Eliminar!",
      cancelButtonText: "Cancelar",
      customClass: {
        popup: "border",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        (async () => {
          setIsLoading(true);
          const result = await deleteRolByIdAsync(token, item.id);
          if (!result.statusResponse) {
            setIsLoading(false);
            if (result.error === "Unauthorized") {
              setIsUnautorized(true);
              return;
            }
            simpleMessage(result.error, "error");
            return;
          }
          if (result.error === "eX01") {
            setIsLoading(false);
            deleteUserData();
            deleteToken();
            setIsLogged(false);
            return;
          }
          setIsLoading(false);
          setLocalReload(!localReload);
          Swal.fire("Eliminado!", "Rol Eliminado.", "success");
        })();
      }
    });
  };

  const actionButtonSx = {
    width: 32,
    height: 32,
    borderRadius: "9px",
    border: `1px solid ${t.fieldLine}`,
    backgroundColor: t.field,
  };

  const puedeAccionar =
    isAccess(access, "SECURITY UPDATE") || isAccess(access, "SECURITY DELETE");

  return (
    <div>
      <Paper sx={{ overflow: "hidden" }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          spacing={2}
          flexWrap="wrap"
          sx={{ padding: "18px 22px", borderBottom: "1px solid", borderColor: "divider" }}
        >
          <SearchField
            value={searchTerm}
            onChange={onChangeSearch}
            placeholder="Buscar rol…"
          />

          {isAccess(access, "SECURITY CREATE") ? (
            <Button
              onClick={() => navigate(`${ruta}/addrol`)}
              startIcon={<AddRoundedIcon />}
              variant="contained"
              color="primary"
              sx={{ whiteSpace: "nowrap", height: "44px" }}
            >
              Agregar rol
            </Button>
          ) : null}
        </Stack>

        {isEmpty(currentItem) ? (
          <NoData />
        ) : (
          <>
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table sx={{ minWidth: 520 }}>
                <TableHead>
                  <TableRow>
                    <TableCell align="center">#</TableCell>
                    <TableCell>Nombre del rol</TableCell>
                    {puedeAccionar ? <TableCell align="center">Acciones</TableCell> : null}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {currentItem.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell align="center" sx={{ color: "text.disabled", fontWeight: 600 }}>
                        {row.id}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "text.primary" }}>{row.rolName}</TableCell>

                      {puedeAccionar ? (
                        <TableCell align="center">
                          <Stack direction="row" spacing={1} justifyContent="center">
                            {isAccess(access, "SECURITY UPDATE") ? (
                              <Tooltip title="Ver detalle">
                                <IconButton
                                  size="small"
                                  sx={{ ...actionButtonSx, color: "primary.main" }}
                                  onClick={() => navigate(`${ruta}/rol/${row.id}`)}
                                >
                                  <VisibilityRoundedIcon sx={{ fontSize: 15 }} />
                                </IconButton>
                              </Tooltip>
                            ) : null}

                            {isAccess(access, "SECURITY DELETE") ? (
                              <Tooltip title="Eliminar">
                                <IconButton
                                  size="small"
                                  sx={{ ...actionButtonSx, color: "error.main" }}
                                  onClick={() => deleteUserRol(row)}
                                >
                                  <DeleteRoundedIcon sx={{ fontSize: 15 }} />
                                </IconButton>
                              </Tooltip>
                            ) : null}
                          </Stack>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <ListFooter
              data={withSearch}
              currentCount={currentItem.length}
              indexFirst={indexFirst}
              paginate={paginate}
              itemsperPage={itemsperPage}
              noun="roles"
            />
          </>
        )}
      </Paper>
    </div>
  );
};

export default RolList;
